<template>
  <Loader
    :value="promises.fetch"
    class="kpb-portal"
    theme="default"
    :class="[`kpb-portal--${theme}`, `kpb-portal--masonry-${layoutComp || 'default'}`]"
  >
    <div class="kpb-portal__breadcrumbs-wrapper" v-if="hasMany && !hideFilter">
      <DropdownComponent class="kpb-portal__breadcrumbs-dropdown" placement="bottom-start">
        <ButtonComponent
          :label="categories.find(element => element.value === current).label"
          @click="hasMany ? () => {} : {}"
        >
          <template #icon>
            <!-- Chevron down -->
            <svg class="kpb-portal__breadcrumbs-dropdown-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path
                d="M441.9 167.3l-19.8-19.8c-4.7-4.7-12.3-4.7-17 0L224 328.2 42.9 147.5c-4.7-4.7-12.3-4.7-17 0L6.1 167.3c-4.7 4.7-4.7 12.3 0 17l209.4 209.4c4.7 4.7 12.3 4.7 17 0l209.4-209.4c4.7-4.7 4.7-12.3 0-17z"
              />
            </svg>
          </template>
        </ButtonComponent>

        <template v-if="hasMany" #dropdown>
          <ButtonComponent
            v-for="(item, index) in categories"
            :key="index"
            :label="item.label"
            @click="current = item.value"
          />
        </template>
      </DropdownComponent>

      <ButtonComponent
        v-for="(item, index) in categories"
        :key="index"
        :label="item.label"
        class="kpb-portal__breadcrumbs-button"
        :class="{ 'kpb-portal__breadcrumbs-button--active': current == item.value }"
        @click="current = item.value"
      />
    </div>

    <slot name="top" />

    <transition-group
      v-if="items && items.length"
      tag="div"
      name="list"
      appear
      class="kpb-portal__items"
      :class="`kpb-portal__items--length-${items.length}`"
    >
      <Card
        v-for="(item, index) in filteredItems"
        :key="index"
        :tag="getTag(item)"
        :target="item.url ? '_blank' : '_self'"
        :href="item.url"
        :theme="theme === 'card' ? 'default' : 'flat'"
        :to="`/${parentSlug || slug}/${item.slug}`"
        :thumbnail="$path('media.cover', item) ? getImage(item.media.cover) : item.cover.url"
        class="kpb-portal__card"
        :class="{ 'kpb-portal__card--disable-nav': !item.url && !item.slug }"
      >
        <template v-if="!getImage(item.media.cover)" #header>
          <!-- house -->
          <svg
            style="opacity: 0.2"
            class="kpb-portal__card-icon"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 576 512"
          >
            <path
              d="M570.53,242,512,190.75V48a16,16,0,0,0-16-16H400a16,16,0,0,0-16,16V78.75L298.53,4a16,16,0,0,0-21.06,0L5.47,242a16,16,0,0,0,21.07,24.09L64,233.27V464a48.05,48.05,0,0,0,48,48H464a48.05,48.05,0,0,0,48-48V233.27l37.46,32.79A16,16,0,0,0,570.53,242ZM480,464a16,16,0,0,1-16,16H112a16,16,0,0,1-16-16V205.27l192-168,192,168Zm0-301.25-64-56V64h64ZM208,218.67V325.34A26.75,26.75,0,0,0,234.66,352H341.3A26.76,26.76,0,0,0,368,325.34V218.67A26.75,26.75,0,0,0,341.3,192H234.66A26.74,26.74,0,0,0,208,218.67ZM240,224h96v96H240Z"
            />
          </svg>
        </template>
        <template #default>
          <component :is="Component" :item="item" :theme="theme" :hide-arrow="hideArrow"></component>
        </template>
      </Card>
    </transition-group>
  </Loader>
</template>

<script>
import Types from './Types'
import Card from '@kvass/card'
import { Capitalize } from '@/utils'
import { DropdownComponent } from 'vue-elder-dropdown'

function getSortValue(value) {
  switch (value) {
    case 'sale':
    case 'rent':
      return 3
    case 'upcoming':
      return 2
    case 'development':
      return 1
    default:
      return 0
  }
}

function getStatus(item) {
  let type = (item?.metadata?.residentialProperties?.saleType || ['sale'])[0]
  let total = item.stats.total

  //published project with no units
  if (item.isPublished && !total) return 'upcoming'

  switch (type) {
    case 'sale':
      if (item.status) return item.status
      if (!item.stats) return 'sold'
      if (item.isPublished && total === item.stats.sold) return 'sold'
      if (item.isPublished && total && item.stats.upcoming && !item.stats.sale) return 'upcoming'
      if (item.isPublished && total) return 'sale'
      return 'development'

    case 'rent':
      if (item.status) return item.status
      if (!item.stats) return 'rented'
      if (item.isPublished && total === item.stats.rented) return 'rented'
      if (item.isPublished && total && item.stats.upcoming && !item.stats.rent) return 'upcoming'
      if (item.isPublished && total) return 'rent'
      return 'development'

    default:
      return 'development'
  }
}

export default {
  props: {
    api: {
      type: Object,
    },

    variant: {
      type: String,
      default: 'project',
      enums: ['project', 'contact', 'article'],
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    excluded: {
      type: Array,
      default: () => [],
    },
    theme: {
      type: String,
      default: 'card',
      enums: ['card', 'masonry', 'tiles', 'thumbnail'],
    },
    disableNav: {
      default: false,
      type: Boolean,
    },

    maxItems: {
      type: Number,
      default: 50,
    },
    hideFilter: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      enums: ['gallery', 'first-project-span', 'second-project-span'],
    },
    hideArrow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      promises: {
        fetch: null,
      },
      items: [],
      current: 'all',
      parentSlug: 'news',
    }
  },
  computed: {
    tag() {
      switch (this.variant) {
        case 'article':
          return 'router-link'
        case 'project':
          return 'a'
        default:
          return 'div'
      }
    },

    layoutComp() {
      if (this.theme !== 'masonry') return

      if (this.layout) return this.layout

      if (this.items.length === 2) return 'gallery'
      return this.maxItems % 2 == 0 ? 'gallery' : 'second-project-span'
    },
    hasItems() {
      return this.data.content
    },
    Component() {
      return Types[Capitalize(this.variant)]
    },
    filteredItems() {
      return this.items.filter(i => {
        if (this.current === 'all') return true
        switch (this.variant) {
          case 'project':
            return (i.address?.county || i.county) === this.current
          case 'article':
            return (i.customFields?.['site-settings'] || {}).category === this.current
          case 'contact':
            return i.categories.includes(this.current)
        }
        return false
      })
    },
    categories() {
      switch (this.variant) {
        case 'project':
          let items = [{ label: this.$tc('all'), value: 'all' }].concat([
            ...new Set(this.items.map(i => i.address?.county || i.county)),
          ])
          return items
            .map(i => {
              if (!i) return {}
              return {
                label: i.label || i,
                value: i.value || i,
              }
            })
            .filter(i => Boolean(i.value))

        case 'article':
          let articleItems = [{ label: this.$tc('all'), value: 'all' }].concat([
            ...new Set(this.items.map(i => (i.customFields['site-settings'] || {}).category)),
          ])
          return articleItems
            .map(i => {
              if (!i) return {}
              return {
                label: this.$tc(i.label || i),
                value: i.value || i,
              }
            })
            .filter(i => Boolean(i.label))

        case 'contact':
          let contactItems = [{ label: this.$tc('all'), value: 'all' }].concat([
            ...new Set(this.items.reduce((acc, curr) => acc.concat(curr.categories), [])),
          ])

          return contactItems
            .map(i => {
              if (!i) return {}
              return {
                label: this.$tc(i.label || i),
                value: i.value || i,
              }
            })
            .filter(i => Boolean(i.value))

        default:
          return []
      }
    },
    hasMany() {
      return this.categories.length > 1
    },
    slug() {
      return this.$route.params?.slug
    },
  },
  methods: {
    getTag(item) {
      if (this.disableNav) return 'div'
      return item.url ? 'a' : 'router-link'
    },
    getImage(value) {
      if (!value || !value.length) return
      let result = value.find(i => {
        if (i.type) return i.type.startsWith('image')
        return i
      })
      if (!result) return
      return result.url
    },

    getData() {
      switch (this.variant) {
        case 'project':
          return this.api
            .getProjects()
            .then(res => {
              return res
                .filter(i => {
                  return !this.excluded.includes(i.id)
                })
                .map(i => {
                  const status = getStatus(i) || 'development'
                  return {
                    sortValue: getSortValue(status),
                    status: status,
                    ...i,
                  }
                })
            })
            .then(i => {
              return this.api.getPosts().then(posts => {
                this.parentSlug = (
                  posts.find(post => ['project-overview'].includes((post?.customFieldsConfig[0] || {}).source)) || {}
                ).slug

                let projectSubpages = posts
                  .filter(post => ['project-subpage'].includes((post?.customFieldsConfig[0] || {}).source))
                  .map(project => {
                    let status = project.customFields?.metadata?.status
                      ? project.customFields?.metadata?.status.split('.')[1]
                      : 'sold'

                    return {
                      ...project,
                      address: {
                        ...project.customFields.address,
                      },
                      url: project.customFields?.['external-link']?.['add-link']
                        ? project.customFields?.['external-link']?.link
                        : '',
                      name: project.title,
                      metadata: {
                        residentialProperties: {
                          saleType: [project.customFields?.metadata?.saleType || 'sale'],
                          propertyType: ([project.customFields?.metadata?.propertyType].flat() || []).filter(Boolean),
                        },
                      },
                      sortValue: getSortValue(status),
                      status: status,
                    }
                  })

                return [...i, ...projectSubpages].sort((a, b) => {
                  if (a.sortValue < b.sortValue) return 1
                  if (a.sortValue > b.sortValue) return -1
                })
              })
            })

        case 'article':
          return this.api.getPosts().then(res => {
            this.parentSlug = (
              res.find(post => ['news'].includes((post?.customFieldsConfig[0] || {}).source)) || {}
            ).slug
            return res.filter(i => {
              return i.customFieldsConfig[0].source === 'article'
            })
          })
      }
    },

    fetch() {
      if (this.hasItems) return (this.items = this.data.content)

      this.promises.fetch = this.getData().then(res => {
        this.items = res.slice(0, this.maxItems)
      })
    },
  },
  created() {
    this.fetch()
  },
  components: {
    Card,
    DropdownComponent,
    ...Types,
  },
}
</script>

<style lang="scss">
.kpb-portal {
  $breakAt: 850px;

  .elder-button {
    border-radius: 0 !important;
    &__label {
      text-align: left;
      padding: 0.5em;
    }
  }

  &__breadcrumbs {
    &-wrapper {
      font-size: 1.2rem;
      font-weight: 500;
      margin-bottom: 3rem;
    }

    &-dropdown {
      @include respond-above('phone') {
        display: none;
      }
      .elder-button {
        background-color: white;
        width: 100%;
        text-align: left;
        padding-left: 0.5rem;
      }
      &-icon {
        display: flex;
        height: 1em;
      }
    }
    &-button {
      border: unset;
      border-radius: unset;
      margin-right: 1rem;

      &:before {
        display: none;
      }
      &:after {
        display: none;
      }

      border-bottom: 2px solid transparent;

      &:hover:not(.kpb-portal__breadcrumbs-button--active) {
        border-bottom: 2px solid rgba(black, 0.6);
      }
      &--active {
        border-bottom: 2px solid black;
      }

      @include respond-below('phone') {
        display: none;
      }
    }
  }

  &__items {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem 1rem;

    @media (max-width: $breakAt) {
      grid-template-columns: 1fr;
    }

    &--length {
      &-1 {
        grid-template-columns: 1fr;
      }
    }
  }

  &__card {
    &-icon {
      height: 3em;
    }
    &--disable-nav {
      &:hover {
        transform: none !important;
      }
    }
  }
  .kvass-card {
    &--default {
      box-shadow: 0px 2px 18px rgba(0, 0, 0, 0.08);
    }
    position: relative;
    transition: transform 0.3s, box-shadow 0.3s ease;
    &:hover {
      text-decoration: none;
      transform: scale(1.02);
    }
  }
  &--thumbnail {
    .kpb-portal__items {
      grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
      gap: 3rem;
    }
    .kvass-card {
      &:hover {
        transform: none;
      }
      background: transparent;
      &__header {
        min-height: 350px;
        border-radius: var(--kvass-card-border-radius);

        position: relative;
        border: none;
      }
      &__content {
        display: flex;
        flex-direction: column;
        padding: 0;
      }
    }
  }
  &--masonry {
    .kpb-portal__items {
      gap: 1rem;
    }
    .kvass-card {
      &__header {
        min-height: 350px;
        height: 100%;
        position: relative;
        border-radius: var(--kvass-card-border-radius);
      }
      &__content {
        padding: 0;
      }
    }

    &-gallery {
    }
    &-first-project-span {
      .kvass-card:nth-child(1) {
        grid-row-end: span 2;
      }
    }
    &-second-project-span {
      .kvass-card:nth-child(2) {
        grid-row-end: span 2;
      }
    }
  }

  &--card {
    .kvass-card {
      &__content {
        padding: 1rem 1.4rem 1.5rem 1.4rem;
      }
      &__header {
        min-height: 350px;
      }
    }
  }

  &--tiles {
    .kpb-portal__items {
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
      @media (max-width: $breakAt) {
        grid-template-columns: 1fr;
      }
    }
    .kvass-card {
      overflow: hidden;
      &__content {
        padding: 2rem;
        min-height: 400px;
      }
      &__header {
        position: absolute;
        width: 100%;
        background: no-repeat;

        background-size: cover;
        background-position: 50%;
        transition: transform 0.62s cubic-bezier(0.05, 0.2, 0.1, 1);

        &:hover {
          transform: scale(1.11);
          -webkit-transform: scale(1.11);
        }
      }

      &:hover {
        transform: none;

        .kpb-project-card {
          &__content {
            background: rgba(0, 0, 0, 0.2);
          }
        }
      }
    }
  }

  .list {
    &-move,
    &-enter-active,
    &-leave-active {
      transition: all 500ms ease;
    }
    &-enter {
      transform: scale(0.95);
    }
    &-enter,
    &-leave-to {
      opacity: 0;
    }
  }
}
</style>
